"use client";

import { Check, Close } from "@ellaandil/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBackInStock, useSelection } from "@trydig/centra/client";
import { getLang } from "@trydig/next/i18n";
import { useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
	DrawerContent,
	DrawerFooter,
	DrawerMain,
	DrawerRoot,
	DrawerViewport,
} from "@/components/ui/Drawer";
import { CheckboxField } from "@/components/ui/form/CheckboxField";
import { InputField } from "@/components/ui/form/InputField";
import { IconButton } from "@/components/ui/IconButton";
import { Swatch } from "@/components/ui/Swatch";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";
import { Link as NextLink } from "@/i18n/routing";
import { useModal } from "@/lib/hooks/useModal";
import type { Item } from "../SizeSelector";
import { type NotifyMeFormInput, notifyMeFormSchema } from "./schema";

export interface NotifyMeModalProps {
	productName: string;
	color: { name?: string | null; hex?: string };
	size: string | null;
	selectedItem?: Item;
}

export const NotifyMeModal = ({ productName, color, size, selectedItem }: NotifyMeModalProps) => {
	const { mutateAsync, isPending } = useBackInStock();
	const { isOpen, setOpen } = useModal("notifyMe");
	const t = useTranslations("notifyMe");
	const { data: countryData } = useSelection({
		select: (data) => {
			return {
				...data.location,
			};
		},
	});
	const tRaw = useTranslations();
	const [submitted, setSubmitted] = useState(false);
	const [showServerError, setShowServerError] = useState(false);

	const methods = useForm<NotifyMeFormInput>({
		// @ts-expect-error zod version mismatch between @ellaandil/web's zod and the zod pulled in transitively via @vercel/functions
		resolver: zodResolver(notifyMeFormSchema),
		defaultValues: { email: "", acceptTerms: undefined },
	});

	useEffect(() => {
		if (!isOpen) {
			setSubmitted(false);
			setShowServerError(false);
			methods.reset();
		}
	}, [isOpen, methods]);

	const onSubmit: SubmitHandler<NotifyMeFormInput> = async (values) => {
		setShowServerError(false);

		if (!selectedItem || !selectedItem.item) return null;

		return await mutateAsync(
			{
				email: values.email,
				item: selectedItem.item,
				shipTo: {
					country: countryData?.country ?? "",
				},
				language: getLang(countryData?.language?.language ?? "en"),
			},
			{
				onSuccess: () => {
					setSubmitted(true);
				},

				onError: (error: unknown) => {
					const fieldErrors =
						typeof error === "object" && error !== null && "errors" in error
							? (error as { errors?: Record<string, string> }).errors
							: undefined;
					const emailMessage = fieldErrors?.email;

					if (emailMessage) {
						methods.setError("email", { message: emailMessage });
					} else {
						setShowServerError(true);
					}
				},
			},
		);
	};

	const resolveError = (message: string | undefined) => (message ? tRaw(message) : undefined);

	return (
		<DrawerRoot open={isOpen} onOpenChange={setOpen}>
			<DrawerViewport className="!max-w-[600px] bg-background-bg-sand-1 backdrop:bg-overlay-3 backdrop:backdrop-blur-[2.5px]">
				<DrawerContent className="!px-0 !gap-0">
					<header className="flex h-12 shrink-0 items-center px-grid-component-spacing-md">
						<div className="flex h-full flex-1 items-center justify-between border-b border-border-medium">
							<Typography variant="body-xs-reg" as="h2" className="text-text-dark-text-1">
								{t("title")}
							</Typography>
							<IconButton
								icon={Close}
								label={t("close")}
								variant="ghost-primary"
								size="lg"
								className="size-9"
								onClick={() => setOpen(false)}
							/>
						</div>
					</header>

					{submitted ? (
						<DrawerMain className="flex flex-col items-center justify-center gap-grid-component-spacing-2xl px-grid-component-spacing-md py-grid-component-spacing-2xl text-center text-text-dark-text-1">
							<Check className="size-8" />
							<div className="flex flex-col gap-grid-component-spacing-2xs">
								<Typography variant="heading-lg" as="h3">
									{t("success.title")}
								</Typography>
								<Typography variant="body-md-reg">{t("success.message")}</Typography>
							</div>
							<Button variant="solid" color="primary" size="large" onClick={() => setOpen(false)}>
								{t("success.continue")}
							</Button>
						</DrawerMain>
					) : (
						<FormProvider {...methods}>
							<form
								noValidate
								onSubmit={methods.handleSubmit(onSubmit)}
								className="flex min-h-0 flex-1 flex-col"
							>
								<DrawerMain className="flex flex-col gap-grid-component-spacing-2xl px-grid-component-spacing-md py-grid-component-spacing-2xl">
									<div className="flex flex-col gap-grid-component-spacing-xs">
										<Typography
											variant="label-sm-reg"
											as="p"
											className="uppercase text-text-dark-text-1"
										>
											{productName}
										</Typography>
										<Typography variant="heading-display-xs" as="p">
											{t("heading")}
										</Typography>
									</div>

									<div className="flex flex-col gap-grid-component-spacing-md">
										<Typography
											variant="label-sm-reg"
											as="p"
											className="uppercase text-text-dark-text-1"
										>
											{t("selectedColor")}
										</Typography>
										<Swatch
											as="div"
											hex={color.hex}
											label={color.name ?? ""}
											active
											className="cursor-default"
										/>
									</div>

									<div className="flex flex-col gap-grid-component-spacing-md">
										<Typography
											variant="label-sm-reg"
											as="p"
											className="uppercase text-text-dark-text-1"
										>
											{t("selectedSize")}
										</Typography>
										<span className="inline-flex w-fit p-grid-component-spacing-2xs">
											<span className="border-b border-border-dark">
												<Typography
													variant="body-sm-reg"
													as="span"
													className="uppercase text-text-dark-text-1"
												>
													{size}
												</Typography>
											</span>
										</span>
									</div>

									<div className="flex flex-col gap-grid-component-spacing-md">
										<InputField
											name="email"
											type="email"
											autoComplete="email"
											label={t("emailLabel")}
											placeholder={t("emailPlaceholder")}
											disabled={isPending}
											errorMessage={resolveError(methods.formState.errors.email?.message)}
										/>
										<CheckboxField
											name="acceptTerms"
											size="large"
											errorMessage={resolveError(methods.formState.errors.acceptTerms?.message)}
										>
											{t("terms.prefix")}{" "}
											<NextLink
												href={t("terms.href")}
												target="_blank"
												rel="noopener noreferrer"
												className="underline"
												onClick={(e) => e.stopPropagation()}
											>
												{t("terms.link")}
											</NextLink>{" "}
											{t("terms.suffix")}
										</CheckboxField>
									</div>

									{showServerError && (
										<Alert
											variant="error"
											title={t("error.title")}
											description={t("error.description")}
										/>
									)}
								</DrawerMain>

								<DrawerFooter className="px-grid-component-spacing-md py-grid-component-spacing-sm">
									<Button
										type="submit"
										variant="solid"
										color="primary"
										size="large"
										loading={isPending}
										className="w-full"
									>
										{t("submit")}
									</Button>
								</DrawerFooter>
							</form>
						</FormProvider>
					)}
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};
